/* eslint-disable */
import React from 'react';
import { useSelector } from 'react-redux';
import Routes from 'routes';
import { CircularProgress, Box } from '@mui/material';
import { useInitializeUserInboxes } from './initializeUserInboxesHook';
import { useUpdateApiPathFromCachedCloudEnv } from './updateApiPathHook';
import { useAuthenticateUser } from './useAuthenticateUser';
import WithLDUser from 'components/WithLDUser';
import Sidebar from 'components/Sidebar';
import AlertDialog from 'components/AlertDialog';
import { useTranslation } from 'react-i18next';
import { useAuth0, useInactivity } from 'customHooks';
import ErrorDialog from './ErrorModal';
import { useStyles } from './styles';

export default function Root({
  handleClose,
  languageSetTo,
  languageCode,
  isLanguageSetToClosestMatch,
}) {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const { user } = useSelector((state) => state);
  const { firstName, lastName, userName } = user;

  /**
   * Logout user and open alert box if user has a session token
   * and has been idle for > 15 mins.
   */
  const [inactive, setInactive] = useInactivity();

  useUpdateApiPathFromCachedCloudEnv();

  const { auth0ClientInitiated } = useAuth0();
  const { isRefreshingToken } = useAuthenticateUser(user, auth0ClientInitiated);

  useInitializeUserInboxes(user);

  React.useEffect(() => {
    i18n.changeLanguage(languageCode || 'en-US');
  }, []);

  React.useEffect(() => {
    if (window.zE) {
      // Zendesk widget
      zE(function () {
        const userFullName = `${firstName} ${lastName}`;
        zE.identify({ name: userFullName, email: userName });
      });
    }
  }, [firstName, lastName, userName]);

  /**
   * This is to prevent displaying Login component while token
   * is refreshing, thus preventing screen flicker.
   */

  if (isRefreshingToken) {
    return (
      <Box justifyContent="center" alignItems="center" className={classes.root}>
        <CircularProgress />
      </Box>
    );
  }

  const isItForBulkText =
    window.location.href.includes('noNavMode=true') ||
    sessionStorage.getItem('noNavMode') === 'true';

  if (isItForBulkText) {
    sessionStorage.setItem('noNavMode', 'true');
  }

  const description = t(
    'your_broswer_is_not_supported_by',
    'Your browser language is not supported by Text Engagement. To continue uninterrupted services, we have set the language to {{Preferred Language}}',
    {
      'Preferred Language': languageSetTo,
    },
  );

  return (
    <Box className={classes.root}>
      <WithLDUser>
        {user.authenticated && !isItForBulkText && <Sidebar />}
        <Routes authenticated={user.authenticated} />
      </WithLDUser>

      <AlertDialog
        open={isLanguageSetToClosestMatch}
        handleClose={() => handleClose(false)}
        title={t('language_support_alert', 'Language Support Alert')}
        description={description}
        btnText={t('ok', 'OK')}
      />
      <AlertDialog
        open={inactive}
        handleClose={() => setInactive(false)}
        title={t('session', 'Session Time Out')}
        description={t(
          'sorry_your_session',
          'Sorry, your session has timed out. Please login again to continue.',
        )}
        btnText={t('ok', 'OK')}
      />
      <ErrorDialog />
    </Box>
  );
}
